import {Box, Typography} from "@mui/material";
import {DataTable} from "../../../Component/Common/Components/DataTable/DataTable";
import {useEffect, useState} from "react";
import {EntriesPerPage} from "../../../Component/Model/Table/Table";
import {
    RequestTrackerService,
} from "../../../Component/Api/Staging/RequestTrackerService";
import {useRecoilValue} from "recoil";
import {OrganisationAtom} from "../../../State/Organisation.Atom";
import {RequestTrackerTableMapper} from "./RequestTrackerTableMapper";
import {toast} from "react-toastify";
import {RequestTracker} from "../../../Component/Model/Dto/Staging/RequestTracker";
import {useHistory} from "react-router-dom";
import {PayrollService} from "../../../Component/Api/Payroll/PayrollService";
import {LoadingButton} from "@mui/lab";
import {Tooltip} from "@mui/material";
import {useSignalRConnection} from "../../../Hooks/useSignalRConnection";

export const PayrollRequests = () => {
    const [entriesPerPage, setEntriesPerPage] = useState<EntriesPerPage>({defaultValue: 5});
    const [totalItemsCount, setTotalItemsCount] = useState<number>(0);
    const [filteredItemCount, setFilteredItemCount] = useState<number>(0);
    const [pageNumber, setPageNumber] = useState<number>(1);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [payrollData, setPayrollData] = useState<RequestTracker.SearchDto[]>([]);
    const organisation = useRecoilValue(OrganisationAtom);
    const [fetchInProgress, setFecthInProgress] = useState<boolean>(false);
    const [hasConnectedPayroll, setHasConnectedPayroll] = useState<boolean>(true);
    const history = useHistory();
    const hub = useSignalRConnection("stagingHub");

    const searchRequest: RequestTracker.SearchRequest = {
        organisationId: organisation.OrganisationId,
        pageNumber: pageNumber,
        pageSize: entriesPerPage.defaultValue!
    };

    const entriesPerPageChanged = (entriesPerPage: number) => {
        setEntriesPerPage({defaultValue: entriesPerPage});
        setPageNumber(1);
    };

    const pageNumberChanged = (pageNumber: number) => {
        setPageNumber(pageNumber);
    };

    const requestEmployees = async () => {
        setIsLoading(true);
        try {
            const response = await RequestTrackerService.requestEmployees(organisation.OrganisationId);
            if (response.data) {
                toast.success("Successfully requested employees.");
                await fetchData();
            } else {
                toast.error(`A request to fetch employees is already in progress.`);
            }

        } catch (error) {
            console.log(error)
            toast.error("An error has occurred");
        }finally {
            setIsLoading(false);
        }
    }

    const fetchData = async () => {
        if (organisation.OrganisationId < 0) return;
        setIsLoading(true);
        setFecthInProgress(true);
        try {
            const searchResult = await RequestTrackerService.searchRequests(searchRequest);
            if (searchResult.data) {
                setPayrollData(searchResult.data.results);
                setTotalItemsCount(searchResult.data.totalItemsCount);
                setFilteredItemCount(searchResult.data.filteredItemsCount);
            }

            const connectedPayrollResult = await PayrollService.hasConnectedPayroll(organisation.OrganisationId);
            setHasConnectedPayroll(connectedPayrollResult.data);
        } catch (error) {
            console.log(error)
        } finally {
            setIsLoading(false);
        }
        setFecthInProgress(false);
    };

    useEffect(() => {
       fetchData();
    }, [pageNumber, entriesPerPage, organisation]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (organisation.OrganisationId < 0) return;

        const eventName = `payrollRequest-${organisation.OrganisationId}`;
        const handler = () => {
            fetchData();
        };

        (async () => {
            try {
                if (!hub.isConnected) {
                    await hub.start();
                }
                hub.on(eventName, handler);
            } catch (error) {
                console.error("SignalR connection failed:", error);
            }
        })();

        return () => {
            hub.off(eventName, handler);
        };
    }, [organisation.OrganisationId]); // eslint-disable-line react-hooks/exhaustive-deps

    const openPayrollImport = () => {
        history.push(`/import/payroll`);
    }

    const openReview = (requestId: number) => {
        history.push(`/organisation/payrollData/review/${requestId}`);
    }

    const retriggerValidation = async (requestId: number) => {
        try {
            const response = await RequestTrackerService.triggerDryRun(requestId);
            if (response.data) {
                toast.success("Validation re-started.");
            } else {
                toast.error("Could not start validation for this request.");
            }
            await fetchData();
        } catch (error) {
            console.log(error);
            toast.error("An error has occurred");
        }
    }

    const payRollTableData = RequestTrackerTableMapper(payrollData, openPayrollImport, openReview, retriggerValidation)

    return (
        <>
            <Typography variant='h4' style={{paddingBottom: '20px'}}>
                Fetch Data from Payroll
            </Typography>
            <Typography variant='body2' style={{paddingBottom: '20px'}}>
                Fetch data from your payroll so that you can import it into your scorecards next
            </Typography>

            <Box mt={1}>
                <Tooltip
                    title={!hasConnectedPayroll ? "No connected payrolls, please connect or contact your administrator" : ""}
                    placement="right">
                    <span>
                    <LoadingButton type="submit"
                               variant='contained' color="primary"
                               onClick={requestEmployees}
                               disabled={fetchInProgress || !hasConnectedPayroll}>
                        Fetch Data
                    </LoadingButton>
                    </span>
                </Tooltip>
            </Box>

            <DataTable
                table={payRollTableData}
                entriesPerPage={entriesPerPage}
                pageNumber={pageNumber}
                filteredItemsCount={filteredItemCount}
                totalItemsCount={totalItemsCount}
                isLoading={isLoading}
                canSearch={false}
                entriesPerPageChanged={entriesPerPageChanged}
                pageNumberChanged={pageNumberChanged}
            />
        </>
    );
}